import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import ClickOutside from 'app/src/elements/ClickListener';
import { customizableColumns, reorder, toggleHidden } from './preferences';

// Portalled and fixed rather than absolute: both tables sit in scroll
// containers that would clip the panel, and the post-sale table's sticky cells
// are at z-index 100.
const PANEL_STYLE = {
  position: 'fixed',
  zIndex: 1000,
  backgroundColor: '#fff',
  border: '1px solid lightgray',
  borderRadius: 4,
  padding: 12,
  minWidth: 220,
  overflowY: 'auto',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
};

const ROW_STYLE = { display: 'flex', alignItems: 'center' };

const BUTTON_GAP = 4;
const VIEWPORT_MARGIN = 8;
// Below this much room under the button, open upward if there's more above.
const MIN_HEIGHT_BELOW = 200;

// Sized to the viewport rather than a fixed share of it: a fixed panel can't
// be scrolled into view, so anything past the bottom edge is unreachable.
const panelPosition = rect => {
  const roomBelow =
    window.innerHeight - rect.bottom - BUTTON_GAP - VIEWPORT_MARGIN;
  const roomAbove = rect.top - BUTTON_GAP - VIEWPORT_MARGIN;

  if (roomBelow < MIN_HEIGHT_BELOW && roomAbove > roomBelow) {
    return {
      bottom: window.innerHeight - rect.top + BUTTON_GAP,
      left: rect.left,
      maxHeight: roomAbove,
    };
  }

  return { top: rect.bottom + BUTTON_GAP, left: rect.left, maxHeight: roomBelow };
};

const samePosition = (a, b) =>
  a.top === b.top && a.bottom === b.bottom && a.left === b.left &&
    a.maxHeight === b.maxHeight;

// `columns` are the source columns in display order, never the ones with
// preferences applied — otherwise a column the user hid drops out of this list.
const ColumnPicker = ({ columns, preferences, onChange, onReset }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const available = customizableColumns(columns);
  const hidden = preferences.hidden || [];

  const anchorToButton = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();

    if (!rect) { return; }

    const next = panelPosition(rect);

    // Most scrolls leave the button where it was — the controls row is sticky —
    // so keep the same object and let React skip the re-render.
    setPosition(current => (samePosition(current, next) ? current : next));
  }, []);

  useEffect(() => {
    if (!open) { return undefined; }

    // Capture, so scrolling any ancestor container re-anchors the panel too.
    document.addEventListener('scroll', anchorToButton, true);
    window.addEventListener('resize', anchorToButton);

    return () => {
      document.removeEventListener('scroll', anchorToButton, true);
      window.removeEventListener('resize', anchorToButton);
    };
  }, [open, anchorToButton]);

  const toggleOpen = () => {
    anchorToButton();
    setOpen(!open);
  };

  const move = (index, offset) => {
    const to = index + offset;

    if (to < 0 || to >= available.length) { return; }

    onChange(reorder(preferences, columns, index, to));
  };

  const panel = (
    <div
      data-testid="column-picker-panel"
      style={{ ...PANEL_STYLE, ...position }}
    >
      {available.map((column, index) => (
        <div key={column.key} style={ROW_STYLE}>
          <input
            id={`column-toggle-${column.key}`}
            type="checkbox"
            checked={!hidden.includes(column.key)}
            onChange={() => onChange(toggleHidden(preferences, column.key))}
          />
          <label
            htmlFor={`column-toggle-${column.key}`}
            style={{ flexGrow: 1, paddingLeft: 6 }}
          >
            {column.label}
          </label>
          <button
            type="button"
            aria-label={`Move ${column.label} up`}
            onClick={() => move(index, -1)}
          >
            ↑
          </button>
          <button
            type="button"
            aria-label={`Move ${column.label} down`}
            onClick={() => move(index, 1)}
          >
            ↓
          </button>
        </div>
      ))}

      <button type="button" onClick={onReset}>Reset</button>
    </div>
  );

  return (
    <div data-testid="column-picker">
      <button ref={buttonRef} type="button" onClick={toggleOpen}>Columns</button>

      {open && createPortal(
        <ClickOutside exceptionRef={buttonRef} onClick={() => setOpen(false)}>
          {panel}
        </ClickOutside>,
        document.body
      )}
    </div>
  );
};

export default ColumnPicker;
